import {
  Component,
  output,
  input,
  computed,
  inject,
  effect,
  OnInit,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { GeographicDataService } from '../../services/geographic-data.service';
import { OrganizationService } from '../../services/organization.service';
import { UserService } from '../../services/user.service';
import type { HistoriesSafrasReportFilters } from '../../types/histories-safras-report.types';
import type { ComplianceReportsFilters } from '../../types/compliances-report.types';
import { AsyncPipe } from '@angular/common';
import { combineLatest, map, Observable, startWith } from 'rxjs';
import { contractOutput } from '#backend/contract';

type Organization = contractOutput['organization']['list']['items'][number];

export interface FilterFieldConfig<R extends ReportFilters = ReportFilters> {
  key: keyof R;
  label: string;
  type:
    | 'text'
    | 'select'
    | 'date-range'
    | 'checkbox-group'
    | 'number'
    | 'autocomplete-org';
  defaultValue?: any;
  adminOnly?: boolean;
  visible?: boolean;
  options?: { value: string; label: string }[];
}

// Export the standardized filters type
export type ReportFilters =
  HistoriesSafrasReportFilters | ComplianceReportsFilters;

@Component({
  selector: 'app-datasafra-search-reports',
  imports: [
    MatFormFieldModule,
    AsyncPipe,
    MatAutocompleteModule,
    MatDatepickerModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatMenuModule,
    MatCheckboxModule,
    MatSelectModule,
    MatButtonModule,
  ],
  templateUrl: './datasafra-search-reports.component.html',
  styleUrl: './datasafra-search-reports.component.scss',
})
export class DatasafraSearchReportsComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly geoService = inject(GeographicDataService);
  private readonly organizationService = inject(OrganizationService);

  states$ = this.geoService.getStatesOptions();
  cities$ = this.geoService.getCitiesOptions();

  // Organizations for autocomplete
  organizations: Organization[] = [];
  filteredOrganizations$!: Observable<Organization[]>;

  // Inputs for dynamic configuration
  filterFields = input.required<FilterFieldConfig[]>();

  resultCount = input<number>();

  filteredCities$!: Observable<{ value: string; label: string }[]>;
  // Output para emitir os filtros aplicados
  filtersChange = output<ReportFilters>();

  downloadClick = output<ReportFilters>();

  // Computed property for visible fields based on user role and module
  visibleFields = computed(() => {
    // const fields = this.filterFields;
    const isAdmin = this.userService.isGeoambiente;

    return this.filterFields()?.filter((field) => {
      // Check if field should be visible
      if (field.visible === false) return false;

      // Check admin-only fields
      if (field.adminOnly && !isAdmin) return false;

      return true;
    });
  });

  // Individual form controls for better control
  formControls: Record<string, FormControl> = {};

  constructor() {
    // Initialize form controls for all possible fields
    effect(() => {
      this.initializeAllFormControls();
      const stateControl = this.formControls['state'];
      const cityControl = this.formControls['city'];
      const organizationControl = this.formControls['organizationId'];

      if (stateControl) {
        stateControl.valueChanges.subscribe((selectedState) => {
          this.geoService.filterCitiesByState(selectedState || '');
          if (cityControl) {
            cityControl.reset();
          }
        });
      }

      if (cityControl) {
        this.filteredCities$ = combineLatest([
          this.cities$,
          cityControl.valueChanges.pipe(startWith('')),
        ]).pipe(
          map(([cities, search]) => {
            const filterValue = (search || '').toLowerCase();
            return cities.filter((city) =>
              city.label.toLowerCase().includes(filterValue),
            );
          }),
        );
      }

      // Setup organization autocomplete
      if (organizationControl) {
        this.filteredOrganizations$ = organizationControl.valueChanges.pipe(
          startWith(''),
          map((value) => {
            if (typeof value === 'string') {
              return this.filterOrganizations(value);
            }
            return this.organizations;
          }),
        );
      }
    });
  }

  async ngOnInit() {
    await this.geoService.initialize();
    await this.loadOrganizations();

    // Initialize filteredOrganizations$ if it hasn't been set yet
    if (!this.filteredOrganizations$) {
      const organizationControl = this.formControls['organizationId'];
      if (organizationControl) {
        this.filteredOrganizations$ = organizationControl.valueChanges.pipe(
          startWith(''),
          map((value) => {
            if (typeof value === 'string') {
              return this.filterOrganizations(value);
            }
            return this.organizations;
          }),
        );
      }
    }
  }

  private async loadOrganizations(): Promise<void> {
    try {
      if (!this.userService.isGeoambiente) return;
      this.organizations = await this.organizationService.getOrganizations(
        false,
        true,
      );
      console.log('Organizações carregadas:', this.organizations.length);
    } catch (error) {
      console.error('Erro ao carregar organizações:', error);
    }
  }

  private filterOrganizations(searchTerm: string): Organization[] {
    if (!searchTerm) {
      return this.organizations;
    }

    const term = searchTerm.toLowerCase();
    return this.organizations.filter((org) =>
      org.name.toLowerCase().includes(term),
    );
  }

  displayOrganizationName = (org: Organization | null): string => {
    return org?.name || '';
  };

  private initializeAllFormControls() {
    // Verifica se existe campo batchRequestId com defaultValue
    const batchRequestIdField = this.filterFields().find(
      (f) => f.key === 'batchRequestId',
    );
    const hasBatchRequestId =
      batchRequestIdField?.defaultValue &&
      batchRequestIdField.defaultValue.trim() !== '';

    // Se há batchRequestId, inicializa onlyMine como false (para ver relatórios da solicitação específica)
    // Caso contrário, inicializa como true (para ver apenas os próprios relatórios)
    this.formControls['onlyMine'] = new FormControl<boolean>(
      !hasBatchRequestId,
      { nonNullable: true },
    );

    // Initialize controls for all possible fields
    this.filterFields().forEach((field) => {
      switch (field.type) {
        case 'text':
        case 'select':
          this.formControls[field.key] = new FormControl<string>('');
          break;
        case 'autocomplete-org':
          this.formControls[field.key] = new FormControl<
            Organization | string | null
          >(null);
          break;
        case 'number':
          this.formControls[field.key] = new FormControl<number | null>(null);
          break;
        case 'date-range':
          this.formControls[`${field.key}From`] = new FormControl<Date | null>(
            null,
          );
          this.formControls[`${field.key}To`] = new FormControl<Date | null>(
            null,
          );
          break;
        case 'checkbox-group':
          field.options?.forEach((option) => {
            this.formControls[`${field.key}_${option.value}`] = new FormControl(
              true,
            );
          });
          break;
      }
    });

    // Observar mudanças no onlyMine para re-emitir os filtros
    this.formControls['onlyMine'].valueChanges.subscribe(() => {
      this.emitFilters();
    });
  }

  // Helper method to get a form control
  getFormControl(controlName: string): FormControl {
    return this.formControls[controlName] || new FormControl('');
  }

  clearFilters() {
    Object.values(this.formControls).forEach((control) => {
      control.reset();
    });
    this.emitFilters();
  }

  applyFilters() {
    this.emitFilters();
  }

  cancel() {
    Object.values(this.formControls).forEach((control) => {
      control.reset();
    });
    this.emitFilters();
  }

  downloadReport() {
    const filters = this.getFiltersValue();
    console.log('Downloading report with filters:', filters);
    this.downloadClick.emit(filters);
  }

  private emitFilters() {
    this.filtersChange.emit(this.getFiltersValue());
  }

  private getFiltersValue(): ReportFilters {
    const filters: ReportFilters = {};

    // Sempre incluir onlyMine com seu valor (default true)
    const onlyMineValue = this.formControls['onlyMine']?.value;
    filters.onlyMine = onlyMineValue ?? true;

    this.visibleFields().forEach((field) => {
      switch (field.type) {
        case 'text':
        case 'number':
        case 'select':
          const value = this.formControls[field.key]?.value;
          if (value) {
            filters[field.key] = value;
          }
          break;
        case 'autocomplete-org': {
          const orgValue = this.formControls[field.key]?.value;
          if (orgValue && typeof orgValue === 'object' && 'id' in orgValue) {
            // É um objeto Organization
            // Se id === -1, significa "Geoambiente" (sem organização), não envia o filtro
            // Se id === null ou undefined, significa "Todos", não envia o filtro
            if (orgValue.id > 0) {
              filters[field.key] = orgValue.id;
            } else if (orgValue.id === -1) {
              // Geoambiente: filtrar por organizationId null
              filters[field.key as 'organizationId'] = null;
            }
            // Se id <= 0 e não é -1, não adiciona filtro (Todos)
          } else if (typeof orgValue === 'number' && orgValue > 0) {
            // É um ID numérico direto positivo
            filters[field.key as 'organizationId'] = orgValue;
          }
          break;
        }
        case 'date-range':
          const fromValue: Date | null =
            this.formControls[`${field.key}From`]?.value;
          const toValue: Date | null =
            this.formControls[`${field.key}To`]?.value;
          if (fromValue || toValue) {
            fromValue?.setHours(0, 0, 0, 0);
            toValue?.setHours(23, 59, 59, 999);

            // @ts-ignore eu nao sei porque tem esse ts-ignore aqui
            filters[field.key] = {
              from: fromValue || undefined,
              to: toValue || undefined,
            };
          }
          break;
        case 'checkbox-group':
          const checkboxValues: any = {};
          field.options?.forEach((option) => {
            const checkboxValue =
              this.formControls[`${field.key}_${option.value}`]?.value;
            if (checkboxValue) {
              checkboxValues[option.value] = true;
            }
          });
          if (Object.keys(checkboxValues).length > 0) {
            filters[field.key] = checkboxValues;
          }
          break;
      }
    });

    return filters;
  }
}
